"use client";

import React, { useState, useEffect } from "react";
import { Menu, Phone, X } from "lucide-react";
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
import { Sheet, SheetContent, SheetTrigger, SheetTitle, SheetDescription, SheetHeader, SheetClose, SheetFooter } from "@/components/ui/sheet";
import { NavigationMenu, NavigationMenuItem, NavigationMenuLink, NavigationMenuList } from "@/components/ui/navigation-menu";
import { MenuProvider, useMenu } from "@/integrations/wordpress/WordPressMenuProvider";
import { cn } from "@/lib/utils";
function DesktopMenuConsumer() {
  const {
    menuItems,
    loading
  } = useMenu();
  if (loading) {
    return <div data-section-id="10106" className="flex gap-4">
        {[1, 2, 3, 4].map(i => <div key={i} className="h-9 w-20 bg-muted/50 animate-pulse rounded-md" />)}
      </div>;
  }
  return <NavigationMenu>
      <NavigationMenuList className="gap-6">
        {menuItems.map((item, i) => <NavigationMenuItem key={item.id} data-index={i}>
            <NavigationMenuLink asChild>
              <Link to={item.href || "#"} className="text-sm font-medium text-foreground/80 hover:text-primary transition-colors hover:underline underline-offset-4 decoration-2">
                {item.label}
              </Link>
            </NavigationMenuLink>
          </NavigationMenuItem>)}
      </NavigationMenuList>
    </NavigationMenu>;
}
function MobileMenuConsumer() {
  const {
    menuItems,
    loading
  } = useMenu();
  if (loading) {
    return <div className="flex flex-col gap-4 mt-8">
        {[1, 2, 3, 4].map(i => <div key={i} className="h-8 w-full bg-muted/50 animate-pulse rounded-md" />)}
      </div>;
  }
  return <nav className="flex flex-col gap-6 mt-8">
      {menuItems.map((item, i) => <div key={item.id} data-index={i} className="border-b border-border/40 pb-4 last:border-0">
          <SheetClose asChild>
            <Link to={item.href || "#"} className="text-lg font-medium text-foreground hover:text-primary transition-colors block">
              {item.label}
            </Link>
          </SheetClose>
        </div>)}
    </nav>;
}
export default function Header() {
  const [isScrolled, setIsScrolled] = useState(false);
  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 10);
    };
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);
  useEffect(() => {
    const handleAnchorClick = (e: MouseEvent) => {
      const target = e.target as HTMLElement;
      const anchor = target.closest("a[href]") as HTMLAnchorElement | null;
      if (anchor) {
        const href = anchor.getAttribute("href");
        if (!href) return;
        let hash = "";
        if (href.startsWith("#")) {
          hash = href;
        } else if (href.startsWith("/") && href.includes("#")) {
          const [path, hashPart] = href.split("#");
          if (path === "/" || path === window.location.pathname) {
            hash = "#" + hashPart;
          }
        }
        if (hash && hash !== "#") {
          const element = document.querySelector(hash);
          if (element) {
            e.preventDefault();
            element.scrollIntoView({
              behavior: "smooth",
              block: "start"
            });
          }
        }
      }
    };
    document.addEventListener("click", handleAnchorClick);
    return () => document.removeEventListener("click", handleAnchorClick);
  }, []);
  return <header id="header" className={cn("sticky top-0 z-50 w-full border-b transition-all duration-300 ease-in-out", isScrolled ? "bg-background/80 backdrop-blur-md border-border shadow-sm" : "bg-background border-transparent shadow-none")}>
      <div className="container mx-auto px-4 h-20 flex items-center justify-between">
        {}
        <div className="flex-shrink-0">
          <Link to="/HomePage" className="flex items-center gap-2">
            <img src="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1597413/img/calcium_hospital_logo.png" alt="Stapes Excellence Center Logo" className="h-8 w-auto object-contain" />
          </Link>
        </div>

        {}
        <div className="hidden md:flex items-center justify-center flex-1 px-8">
          <MenuProvider menu_id="80" className="w-full flex justify-center">
            <DesktopMenuConsumer />
          </MenuProvider>
        </div>

        {}
        <div className="flex items-center gap-4">
          {}
          <div className="hidden md:block">
            <Button variant="default" size="lg" className="font-semibold shadow-md hover:shadow-lg transition-all animate-in fade-in zoom-in duration-300 bg-primary text-primary-foreground rounded-sm" asChild>
              <Link to="/PatientPortalPage">
                <Phone className="size-4 mr-2" />
                Patient Portal
              </Link>
            </Button>
          </div>

          {}
          <div className="md:hidden">
            <Sheet>
              <SheetTrigger asChild>
                <Button variant="ghost" size="icon" className="shrink-0">
                  <Menu className="size-6 text-foreground" />
                  <span className="sr-only">Toggle menu</span>
                </Button>
              </SheetTrigger>
              <SheetContent side="right" className="w-[300px] sm:w-[400px] flex flex-col bg-background border-l border-border">
                <SheetHeader className="text-left border-b border-border pb-4">
                  <SheetTitle>
                    <img src="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1597413/img/calcium_hospital_logo.png" alt="Stapes Excellence Center" className="h-7 w-auto object-contain" />
                  </SheetTitle>
                  <SheetDescription className="text-muted-foreground font-sans">
                    Global Leaders in Stapes Surgery & Otosclerosis Care
                  </SheetDescription>
                </SheetHeader>
                
                <div className="flex-1 overflow-y-auto py-4">
                  <MenuProvider menu_id="80">
                    <MobileMenuConsumer />
                  </MenuProvider>
                </div>

                <SheetFooter className="mt-auto border-t border-border pt-4">
                  <Button variant="default" className="w-full font-semibold justify-center bg-primary text-primary-foreground rounded-sm" asChild>
                    <Link to="/PatientPortalPage">
                      <Phone className="size-4 mr-2" />
                      Patient Portal
                    </Link>
                  </Button>
                </SheetFooter>
              </SheetContent>
            </Sheet>
          </div>
        </div>
      </div>
    </header>;
}